import React, { useEffect, useState } from 'react';
import { reviewApi, COMMENT_MODE } from '../../utils/reviewApi';
import { FileChip, Modal, PeoplePicker } from './reviewUi';

/** Two weeks out, as the default due date — a review with no deadline never ends. */
function defaultDue() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

/**
 * Start a review of one draft (mockup screen 2).
 *
 * Several reviews may run on one revision (D5), so when this revision already has an open
 * one the dialog says so and suggests a different name — it never blocks.
 */
export default function StartReviewModal({ setupId, row, onClose, onStarted }) {
  const [people, setPeople] = useState(null);
  const [existing, setExisting] = useState([]);
  const [name, setName] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [due, setDue] = useState(defaultDue());
  const [message, setMessage] = useState('');
  const [aiPre, setAiPre] = useState(false);
  const [seeEach, setSeeEach] = useState(true);
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    reviewApi.users().then((d) => { if (live) setPeople(d.people || []); })
      .catch((e) => { if (live) { setPeople([]); setError(e.message); } });
    reviewApi.itemReviews(row.item_id, setupId).then((d) => {
      if (!live) return;
      const cur = (d.revisions || []).find((g) => g.revision === String(row.revision || '').toUpperCase());
      const all = cur?.reviews || [];
      setExisting(all.filter((r) => r.status === 'open'));
      // Several reviews may run on one revision (D5). Pre-fill a name that differs from the
      // ones already there, so a second review starts with one click — it can be renamed.
      const taken = new Set(all.map((r) => String(r.name || '').trim().toLowerCase()));
      let n = all.length + 1;
      let suggestion = all.length ? `Review ${n}` : 'Review';
      while (taken.has(suggestion.toLowerCase())) { n += 1; suggestion = `Review ${n}`; }
      setName(suggestion);
    }).catch(() => { if (live) setName('Review'); });
    return () => { live = false; };
  }, [row.item_id, row.revision, setupId]);

  const submit = async () => {
    setError('');
    if (!name.trim()) { setError('Give the review a name.'); return; }
    if (!reviewers.length) { setError('Add at least one reviewer.'); return; }
    setBusy(true);
    try {
      const res = await reviewApi.start({
        setup_id: setupId, item_id: row.item_id, item_key: row.key, item_name: row.name,
        revision: row.revision, file_name: row.file_name, routing_state: row.routing_state,
        name: name.trim(), reviewers, due_date: due, message: message.trim(),
        ai_prereview: aiPre, see_each_other: seeEach, email_reviewers: email,
      });
      onStarted(res.id);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const round = 'Round 1';
  return (
    <Modal
      title="Start review"
      subtitle={<><strong style={{ color: '#5C35A8' }}>{row.key}</strong> {row.name} · Rev {row.revision} draft · {round}</>}
      onClose={onClose}
      footer={(
        <>
          {error && <div className="drv-error drv-foot-error" role="alert">{error}</div>}
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={submit} disabled={busy}>
            {busy ? 'Starting…' : 'Start review'}
          </button>
        </>
      )}
    >
      <div className="drv-filecard">
        <FileChip fileName={row.file_name} kind={row.file_kind} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{row.file_name || 'No file name in the filter'}</div>
          <div className="drv-small">
            {COMMENT_MODE[row.file_kind] || COMMENT_MODE.other}
            {row.last_approved ? ` · replaces approved Rev ${row.last_approved}` : ''}
          </div>
        </div>
      </div>

      {existing.length > 0 && (
        <div className="drv-note">
          Rev {row.revision} already has {existing.length} open review{existing.length === 1 ? '' : 's'}:{' '}
          {existing.map((r, i) => (
            <React.Fragment key={r.id}>
              {i > 0 && ', '}<strong>{r.name}</strong> (round {r.current_round},{' '}
              {r.progress?.reviewers_done ?? 0} of {r.progress?.reviewers_total ?? 0} done)
            </React.Fragment>
          ))}. This starts a separate review on the same revision.
        </div>
      )}

      <label className="drv-field">
        <span className="drv-lbl">Review name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. Technical review, QA & regulatory review" maxLength={120} />
      </label>

      <div className="drv-field">
        <span className="drv-lbl">Reviewers <span>— people in this account who have signed in here</span></span>
        <PeoplePicker people={people} value={reviewers} onChange={(v) => { setReviewers(v); setError(''); }} placeholder="Type a name…" />
      </div>

      <div className="drv-row">
        <label className="drv-field">
          <span className="drv-lbl">Due date</span>
          <input className="drv-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
      </div>

      <label className="drv-field">
        <span className="drv-lbl">Message to reviewers</span>
        <textarea className="drv-textarea" value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="What changed, and where to focus." maxLength={2000} />
      </label>

      <div className="drv-chk-group">
        <label className="drv-chk">
          <input type="checkbox" checked={aiPre} onChange={(e) => setAiPre(e.target.checked)} />
          <span><strong>AI pre-review first</strong><br />
            <span className="drv-muted">Claude checks the draft for unclear wording, missing sections and
              changes from the approved revision. Its notes appear as comments marked AI. (Coming in a later
              release — ticking it now records the request.)</span></span>
        </label>
        <label className="drv-chk">
          <input type="checkbox" checked={seeEach} onChange={(e) => setSeeEach(e.target.checked)} />
          <span><strong>Reviewers see each other’s comments</strong></span>
        </label>
        <label className="drv-chk">
          <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
          <span><strong>Also email reviewers</strong>{' '}
            <span className="drv-muted">— they always get the bell notification and see it under “Waiting for you”</span></span>
        </label>
      </div>

    </Modal>
  );
}
